// libs
import React, { memo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useDomainConfig } from 'shared/domainConfig/context/context';

// helpers
import Storage from 'shared/helpers/storage';

// styles
import { NudgeWrapper, NudgeTooltip, Arrow } from './styles';

// constants
import { STORAGE_KEY, SESSION_KEY } from './constants';

interface IAccountsNudgeProps {
  children: React.ReactNode;
}

function AccountsNudge({ children }: IAccountsNudgeProps) {
  const [showNudge, setShowNudge] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; right: number; } | null>(
    null
  );
  const timerRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { SHOW_HEADER_ACCOUNT } = useDomainConfig();
  const nudgeConfig = useRemoteConfig('accountsNudge');

  const { durationMs, text, limit } = nudgeConfig;

  useEffect(() => {
    if (!SHOW_HEADER_ACCOUNT || !text || limit <= 0) {
      return;
    }

    try {
      if (sessionStorage.getItem(SESSION_KEY)) {
        return;
      }

      const impressionCount = Storage.getItem<number>(STORAGE_KEY, 0) || 0;

      if (impressionCount >= limit) {
        return;
      }

      setShowNudge(true);
      Storage.setItem(STORAGE_KEY, impressionCount + 1);
      sessionStorage.setItem(SESSION_KEY, '1');

      timerRef.current = window.setTimeout(() => {
        setShowNudge(false);
      }, durationMs);
    } catch {
      // storage unavailable — silently skip
    }

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [SHOW_HEADER_ACCOUNT, durationMs, text, limit]);

  useEffect(() => {
    if (showNudge && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [showNudge]);

  if (!showNudge) {
    return <>{children}</>;
  }

  return (
    <NudgeWrapper ref={wrapperRef}>
      {children}
      {tooltipPosition &&
        createPortal(
          <NudgeTooltip
            data-test="accounts-nudge-tooltip"
            style={{ top: tooltipPosition.top, right: tooltipPosition.right }}
          >
            <Arrow />
            {text}
          </NudgeTooltip>,
          document.body
        )}
    </NudgeWrapper>
  );
}

export default memo(AccountsNudge);
